import { authenticatedProApiFetch } from './pro-api.client';

export interface CertificationModule {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  heroImageUrl: string | null;
  trophyImageUrl?: string | null;
  certificateHeaderText?: string | null;
  certificateCourseText?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface CertificationTraining {
  id: string;
  moduleId: string;
  stepNumber: number;
  section: string;
  title: string;
  /**
   * Hosted training video URL. May be a CloudFront/S3 (or local) file URL from upload,
   * a direct video file URL, or a YouTube watch/short/embed URL (hotlinked, not re-hosted).
   * Detect YouTube via `isYoutubeUrl` / `getYoutubeVideoId` — there is no separate source column.
   */
  videoUrl: string | null;
  /**
   * Poster image. Uploaded CDN image for file videos, or a hotlinked
   * `img.youtube.com` URL when `videoUrl` is a YouTube link.
   */
  thumbnailUrl?: string | null;
  transcript: string | null;
  /** Optional commentary shown between the video and the actions row. */
  videoCommentary: string | null;
  sortOrder: number;
  /**
   * Resolved media duration in whole seconds (server-populated on video save).
   * Prefer this over client probing when present.
   */
  videoDurationSeconds?: number | null;
  /**
   * Quiz question count for student course views (no question text/answers).
   * Present on `GET /certifications/:moduleId`; may be absent on list/admin payloads.
   */
  questionCount?: number;
  /**
   * Caller's best attempt summary for this training (student module detail only).
   */
  userProgress?: CertificationTrainingUserProgress | null;
  /**
   * High-water percent of this lesson's video the caller has actually played
   * (seek jumps do not count). Student payloads only; 0 when never watched.
   */
  videoWatchedPercent?: number;
  /** Accumulated play time in seconds (student training GET). */
  watchedSeconds?: number;
  /** Present on admin module detail (`/certifications/:id/admin`) only. */
  questions?: CertificationTrainingQuestionAdmin[];
}

export interface CertificationTrainingUserProgress {
  passed: boolean;
  score: number;
  correctCount: number;
  totalCount: number;
  attemptedAt: string;
  /** Current (non-stale) quiz attempts for this training, when provided by the API. */
  attemptCount?: number;
}

export interface CertificationModuleDetail extends CertificationModule {
  trainings: CertificationTraining[];
  /** Present on student `GET /certifications/:moduleId` when the user is enrolled. */
  enrollment?: Pick<
    Certification,
    'id' | 'status' | 'progressPercent' | 'completedAt' | 'expiresAt' | 'certificateNumber'
  > | null;
}

/** Quiz question including correctChoiceIndex — admin payloads only. */
export interface CertificationTrainingQuestionAdmin {
  id: string;
  trainingId: string;
  text: string;
  choices: string[];
  correctChoiceIndex: number;
  sortOrder: number;
}

export type CertificationStatus = 'not_started' | 'in_progress' | 'retake_needed' | 'complete';

export interface Certification {
  id: string;
  userId: string;
  moduleId: string;
  status: CertificationStatus;
  progressPercent: number;
  completedAt: string | null;
  expiresAt: string | null;
  /** Passed trainings / total trainings — present on GET /certifications/me. */
  passedCount?: number;
  totalCount?: number;
  certificateNumber?: string | null;
}

export interface CertificationTrainingQuestion {
  id: string;
  trainingId: string;
  text: string;
  choices: string[];
  sortOrder: number;
}

export interface CertificationTrainingAttempt {
  id: string;
  userId: string;
  trainingId: string;
  status: string;
  correctCount: number;
  totalCount: number;
  score: number;
  passed: boolean;
  attemptedAt: string;
}

export interface CertificationResource {
  id: string;
  type: 'video' | 'pdf';
  title: string;
  url: string;
  sortOrder: number;
}

export async function fetchCertificationModules(
  accessToken: string,
  options?: { includeInactive?: boolean },
): Promise<CertificationModule[]> {
  const path = options?.includeInactive ? '/certifications?includeInactive=true' : '/certifications';
  const res = await authenticatedProApiFetch<{ modules: CertificationModule[] }>(path, accessToken);
  return res.modules;
}

export async function fetchCertificationModule(
  accessToken: string,
  moduleId: string,
): Promise<CertificationModuleDetail> {
  return authenticatedProApiFetch<CertificationModuleDetail>(`/certifications/${moduleId}`, accessToken);
}

/**
 * Admin module detail with nested quiz questions (including correctChoiceIndex).
 * Used for export preview and other authoring tools — not the student course flow.
 */
export async function fetchCertificationModuleAdmin(
  accessToken: string,
  moduleId: string,
): Promise<CertificationModuleDetail> {
  return authenticatedProApiFetch<CertificationModuleDetail>(
    `/certifications/${moduleId}/admin`,
    accessToken,
  );
}

export async function enrollInCertificationModule(
  accessToken: string,
  moduleId: string,
): Promise<Certification> {
  return authenticatedProApiFetch<Certification>(`/certifications/${moduleId}/enroll`, accessToken, {
    method: 'POST',
  });
}

export async function fetchMyCertifications(accessToken: string): Promise<Certification[]> {
  const res = await authenticatedProApiFetch<{ certifications: Certification[] }>('/certifications/me', accessToken);
  return res.certifications;
}

export async function fetchTraining(
  accessToken: string,
  trainingId: string,
): Promise<CertificationTraining & { questions: CertificationTrainingQuestion[] }> {
  return authenticatedProApiFetch(`/certification-trainings/${trainingId}`, accessToken);
}

/**
 * Admin-only variant of {@link fetchTraining} whose questions include the real
 * `correctChoiceIndex` (the student-facing route withholds it). Used to prefill
 * the admin edit form so a full-replace PATCH doesn't silently zero out the
 * correct answer for questions the admin didn't intend to touch.
 */
export async function fetchTrainingForEdit(
  accessToken: string,
  trainingId: string,
): Promise<
  CertificationTraining & {
    questions: Array<CertificationTrainingQuestionInput & { id: string }>;
  }
> {
  return authenticatedProApiFetch(`/certification-trainings/${trainingId}/admin`, accessToken);
}

export async function checkTrainingAnswer(
  accessToken: string,
  trainingId: string,
  questionId: string,
  choiceIndex: number,
): Promise<{ correct: boolean }> {
  return authenticatedProApiFetch<{ correct: boolean }>(
    `/certification-trainings/${trainingId}/questions/${questionId}/check`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ choiceIndex }) },
  );
}

export async function submitTrainingAttempt(
  accessToken: string,
  trainingId: string,
  answers: { questionId: string; choiceIndex: number }[],
): Promise<CertificationTrainingAttempt> {
  return authenticatedProApiFetch<CertificationTrainingAttempt>(
    `/certification-trainings/${trainingId}/attempts`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ answers }) },
  );
}

export async function patchTrainingVideoProgress(
  accessToken: string,
  trainingId: string,
  watchedSeconds: number,
  durationSeconds?: number,
): Promise<{ watchedSeconds: number; videoWatchedPercent: number }> {
  const body: { watchedSeconds: number; durationSeconds?: number } = {
    watchedSeconds,
  };
  if (durationSeconds != null && durationSeconds > 0) {
    body.durationSeconds = durationSeconds;
  }
  return authenticatedProApiFetch(
    `/certification-trainings/${trainingId}/video-progress`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function fetchCertificationResources(accessToken: string): Promise<CertificationResource[]> {
  const res = await authenticatedProApiFetch<{ resources: CertificationResource[] }>(
    '/certification-resources',
    accessToken,
  );
  return res.resources;
}

// ─── Admin: Certification Modules ─────────────────────────────────────────────

export interface CreateCertificationModulePayload {
  title: string;
  slug: string;
  description?: string | null;
  heroImageUrl?: string | null;
  trophyImageUrl?: string | null;
  certificateHeaderText?: string | null;
  certificateCourseText?: string | null;
  sortOrder?: number;
  isActive: boolean;
}
export type UpdateCertificationModulePayload = Partial<CreateCertificationModulePayload>;

export async function createCertificationModule(
  accessToken: string,
  payload: CreateCertificationModulePayload,
): Promise<CertificationModule> {
  return authenticatedProApiFetch<CertificationModule>('/certifications', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCertificationModule(
  accessToken: string,
  moduleId: string,
  payload: UpdateCertificationModulePayload,
): Promise<CertificationModule> {
  return authenticatedProApiFetch<CertificationModule>(`/certifications/${moduleId}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteCertificationModule(
  accessToken: string,
  moduleId: string,
): Promise<void> {
  await authenticatedProApiFetch(`/certifications/${moduleId}`, accessToken, {
    method: 'DELETE',
  });
}

export interface HeroImageUploadInstructions {
  uploadUrl: string;
  /** Field name matches the shared avatar-storage response shape verbatim (it's reused for hero images too). */
  avatarUrl: string;
  key: string;
  contentType: string;
  expiresInSeconds: number;
}

export async function requestHeroImageUpload(
  accessToken: string,
  contentType: string,
): Promise<HeroImageUploadInstructions> {
  return authenticatedProApiFetch<HeroImageUploadInstructions>(
    '/certifications/hero-image/upload-url',
    accessToken,
    { method: 'POST', body: JSON.stringify({ contentType }) },
  );
}

export async function requestTrophyImageUpload(
  accessToken: string,
  contentType: string,
): Promise<HeroImageUploadInstructions> {
  return authenticatedProApiFetch<HeroImageUploadInstructions>(
    '/certifications/trophy-image/upload-url',
    accessToken,
    { method: 'POST', body: JSON.stringify({ contentType }) },
  );
}

// ─── Admin: Technicians ───────────────────────────────────────────────────────

export interface TechnicianModuleProgress {
  moduleId: string;
  moduleTitle: string;
  progressPercent: number;
  completedAt: string | null;
  status: CertificationStatus;
  expiresAt: string | null;
  trophyImageUrl?: string | null;
  certificateNumber?: string | null;
}

export interface Technician {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  modules: TechnicianModuleProgress[];
}

export async function fetchTechnicians(accessToken: string): Promise<Technician[]> {
  const res = await authenticatedProApiFetch<{ technicians: Technician[] }>(
    '/certifications/admin/technicians',
    accessToken,
  );
  return res.technicians;
}

/**
 * Admin: a single user's per-module certification progress.
 *
 * Calls the nested-resource route `/certifications/admin/technicians/:userId`
 * (lion-energy-user-service's `src/routes/certifications.ts`, proxied by pro-api).
 * Returns a 404 (surfaced as a thrown error) if the user has no certification records.
 */
export async function fetchUserCertifications(
  accessToken: string,
  userId: string,
): Promise<TechnicianModuleProgress[]> {
  const res = await authenticatedProApiFetch<{ modules: TechnicianModuleProgress[] }>(
    `/certifications/admin/technicians/${userId}`,
    accessToken,
  );
  return res.modules;
}

// ─── Admin: Legacy progress claims ────────────────────────────────────────────

export type ProgressClaimStatus = "pending" | "claimed";

export interface ProgressClaimClaimedBy {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface ProgressClaim {
  id: string;
  email: string;
  legacyUserId: string | null;
  source: string;
  createdAt: string;
  claimedAt: string | null;
  claimedByUserId: string | null;
  claimedBy: ProgressClaimClaimedBy | null;
  moduleCount: number;
  trainingCount: number;
  moduleSlugs: string[];
}

export async function fetchProgressClaims(
  accessToken: string,
  opts?: { status?: "all" | ProgressClaimStatus; search?: string },
): Promise<ProgressClaim[]> {
  const params = new URLSearchParams();
  if (opts?.status && opts.status !== "all") params.set("status", opts.status);
  if (opts?.search?.trim()) params.set("search", opts.search.trim());
  const qs = params.toString();
  const res = await authenticatedProApiFetch<{ claims: ProgressClaim[] }>(
    `/certifications/admin/progress-claims${qs ? `?${qs}` : ""}`,
    accessToken,
  );
  return res.claims ?? [];
}

export interface LegacyCertTrainingProgress {
  stepNumber: number;
  passed: boolean;
  score: number;
  correctCount: number;
  totalCount: number;
  attemptedAt?: string;
  videoWatched?: boolean;
}

export interface LegacyCertModuleProgress {
  moduleSlug: string;
  completedAt?: string | null;
  certNum?: string | null;
  trainings: LegacyCertTrainingProgress[];
}

export interface LegacyCertProgressPayload {
  v: 1;
  legacyUserId?: string;
  modules: LegacyCertModuleProgress[];
}

export interface ProgressClaimDetail extends ProgressClaim {
  payload: LegacyCertProgressPayload;
}

export interface MigrationPreviewTraining {
  stepNumber: number;
  passed: boolean;
  score: number;
  correctCount: number;
  totalCount: number;
  attemptedAt?: string;
  videoWatched?: boolean;
  mappedTrainingId: string | null;
  mappedTrainingTitle: string | null;
  mappingStatus: "mapped" | "unmapped";
  section?: string | null;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  videoDurationSeconds?: number | null;
}

export type MigrationPreviewCarryover =
  | "full_credit"
  | "partial_reset"
  | "mapped"
  | null;

export interface MigrationPreviewModule {
  moduleSlug: string;
  certNum: string | null;
  completedAt: string | null;
  mappedModuleId: string | null;
  mappedModuleTitle: string | null;
  heroImageUrl?: string | null;
  trophyImageUrl?: string | null;
  mappingStatus: "mapped" | "unmapped_module";
  projectedStatus: string | null;
  projectedProgressPercent: number | null;
  passedCount: number;
  totalMappedTrainings: number;
  /** Canonical legacy curriculum size (hardcoded catalog), not captured attempts. */
  legacyTotalSteps?: number;
  /**
   * Reformatted-course policy:
   * - full_credit: legacy complete → credit all live trainings
   * - partial_reset: legacy partial → no progress carried over
   * - mapped: step-for-step (format unchanged)
   */
  carryover?: MigrationPreviewCarryover;
  /** True when legacy curriculum size differs from the live course. */
  formatChanged?: boolean;
  trainings: MigrationPreviewTraining[];
}

export interface MigrationPreviewResult {
  claim: ProgressClaimDetail;
  existingUser: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  } | null;
  alreadyClaimed: boolean;
  applyPath: "apply_immediately" | "pending_claim" | "already_claimed";
  modules: MigrationPreviewModule[];
  unmappedTrainings: number;
}

export async function fetchProgressClaim(
  accessToken: string,
  claimId: string,
): Promise<ProgressClaimDetail> {
  const res = await authenticatedProApiFetch<{ claim: ProgressClaimDetail }>(
    `/certifications/admin/progress-claims/${claimId}`,
    accessToken,
  );
  return res.claim;
}

export async function previewProgressClaimMigration(
  accessToken: string,
  claimId: string,
): Promise<MigrationPreviewResult> {
  return authenticatedProApiFetch<MigrationPreviewResult>(
    `/certifications/admin/progress-claims/${claimId}/preview`,
    accessToken,
    { method: "POST" },
  );
}

// ─── Admin: Certification Trainings ───────────────────────────────────────────

export interface CertificationTrainingQuestionInput {
  id?: string;
  text: string;
  choices: string[];
  correctChoiceIndex: number;
  sortOrder: number;
}

export interface CertificationTrainingInput {
  id?: string;
  moduleId: string;
  stepNumber?: number;
  section?: string | null;
  title: string;
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  /** Optional client-probed duration hint; server prefers its own resolution. */
  videoDurationSeconds?: number | null;
  transcript?: string | null;
  videoCommentary?: string | null;
  sortOrder?: number;
  questions: CertificationTrainingQuestionInput[];
}

export type UpdateCertificationTrainingPayload = Partial<CertificationTrainingInput>;

export async function createCertificationTraining(
  accessToken: string,
  payload: CertificationTrainingInput,
): Promise<CertificationTraining> {
  return authenticatedProApiFetch<CertificationTraining>('/certification-trainings', accessToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCertificationTraining(
  accessToken: string,
  trainingId: string,
  payload: UpdateCertificationTrainingPayload,
): Promise<CertificationTraining> {
  return authenticatedProApiFetch<CertificationTraining>(`/certification-trainings/${trainingId}`, accessToken, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteCertificationTraining(
  accessToken: string,
  trainingId: string,
): Promise<void> {
  await authenticatedProApiFetch(`/certification-trainings/${trainingId}`, accessToken, {
    method: 'DELETE',
  });
}

export interface TrainingVideoUploadInstructions {
  uploadUrl: string;
  videoUrl: string;
  key: string;
  contentType: string;
  expiresInSeconds: number;
}

export async function requestTrainingVideoUpload(
  accessToken: string,
  trainingId: string,
  contentType: string,
): Promise<TrainingVideoUploadInstructions> {
  return authenticatedProApiFetch<TrainingVideoUploadInstructions>(
    `/certification-trainings/${trainingId}/video/upload-url`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ contentType }) },
  );
}

export async function requestTrainingThumbnailUpload(
  accessToken: string,
  trainingId: string,
  contentType: string,
): Promise<HeroImageUploadInstructions> {
  return authenticatedProApiFetch<HeroImageUploadInstructions>(
    `/certification-trainings/${trainingId}/thumbnail/upload-url`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ contentType }) },
  );
}
